import { useId, useRef, type KeyboardEvent, type ReactNode } from 'react';
import styles from './Tabs.module.css';

/**
 * Tabs inside a card (the job builder's Basics, Redesign Group 2): one row of tabs over one
 * panel, the open tab underlined in the section colour. A tab may carry a badge ("2 to fix") or
 * a quiet note ("optional") after its name, and turns green once it is done.
 *
 * WAI-ARIA tabs with automatic activation: the arrow keys move along the row and open the tab
 * they land on, Home and End jump to the ends, and only the open tab is a Tab stop (06-qol §2).
 * Switching is a component swap; nothing is fetched (CLAUDE.md §5a).
 */

export interface TabItem<T extends string> {
  readonly value: T;
  readonly label: string;
  /** Drawn after the label: a count badge, or a quiet word. */
  readonly after?: ReactNode;
  /** Read after the label by a screen reader, where `after` is a bare number. */
  readonly afterLabel?: string;
  /** Everything the tab needs is filled: the tab turns green (Request a hire, owner 2026-11-01). */
  readonly isDone?: boolean;
}

interface TabsProps<T extends string> {
  readonly label: string;
  readonly tabs: readonly TabItem<T>[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  /** The open tab's panel. */
  readonly children: ReactNode;
}

const STEPS: Readonly<Record<string, 1 | -1>> = { ArrowRight: 1, ArrowLeft: -1 };

export function Tabs<T extends string>({ label, tabs, value, onChange, children }: TabsProps<T>): ReactNode {
  const baseId = useId();
  const row = useRef<HTMLDivElement>(null);
  const tabId = (tab: T): string => `${baseId}-tab-${tab}`;
  const panelId = `${baseId}-panel`;

  function open(index: number): void {
    const next = tabs[(index + tabs.length) % tabs.length];
    if (!next) return;
    onChange(next.value);
    row.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[tabs.indexOf(next)]?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number): void {
    const step = STEPS[event.key];
    if (step !== undefined) {
      event.preventDefault();
      open(index + step);
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      open(event.key === 'Home' ? 0 : tabs.length - 1);
    }
  }

  return (
    <div className={styles.tabs}>
      <div ref={row} className={styles.row} role="tablist" aria-label={label}>
        {tabs.map((tab, index) => {
          const isOpen = tab.value === value;
          return (
            <button
              key={tab.value}
              id={tabId(tab.value)}
              type="button"
              role="tab"
              className={styles.tab}
              aria-selected={isOpen}
              aria-controls={panelId}
              data-done={tab.isDone || undefined}
              tabIndex={isOpen ? 0 : -1}
              onClick={() => onChange(tab.value)}
              onKeyDown={(event) => onKeyDown(event, index)}
            >
              {tab.label}
              {tab.after !== undefined && (
                <span className={styles.after} aria-hidden={tab.afterLabel !== undefined || undefined}>
                  {tab.after}
                </span>
              )}
              {tab.afterLabel !== undefined && <span className="sr-only">, {tab.afterLabel}</span>}
            </button>
          );
        })}
      </div>
      <div id={panelId} role="tabpanel" aria-labelledby={tabId(value)} className={styles.panel}>
        {children}
      </div>
    </div>
  );
}
